"use client";

import { useMemo } from "react";
import dynamic from "next/dynamic";
import { Box, Card, CardContent, Divider, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";

import rawData from "@/_data/dashboard/bubble_chart_data.json";
import { formatBillions } from "../utils";

const ReactECharts = dynamic(() => import("echarts-for-react"), { ssr: false });

type DataRow = {
  source: string;
  target: string;
  "SUM(TotalCommitmentAmount)": number | null;
};

const ALL_ROWS = rawData.result[0].data as DataRow[];

const NODE_COLORS = [
  "#6b7280",
  "#3b82f6",
  "#8b5cf6",
  "#06b6d4",
  "#10b981",
  "#f59e0b",
  "#f97316",
  "#f43f5e",
  "#0ea5e9",
  "#84cc16",
  "#ec4899",
];

function stripPrefix(name: string): string {
  return name.replace(/^(CR|BR):\s*/, "").trim();
}

export default function RegionalDebtFlowNetwork() {
  const option = useMemo<EChartsOption>(() => {
    // Only draw edges with a real flow value
    const rows = ALL_ROWS.filter(
      (r) => (r["SUM(TotalCommitmentAmount)"] ?? 0) > 0,
    );

    // Total volume per node
    const totals: Record<string, number> = {};
    ALL_ROWS.forEach((row) => {
      const val = row["SUM(TotalCommitmentAmount)"] ?? 0;
      totals[row.source] = (totals[row.source] ?? 0) + val;
      totals[row.target] = (totals[row.target] ?? 0) + val;
    });

    // Unique nodes from valid rows, sorted largest first so biggest bubbles
    // appear at the top of each column
    const creditorSet = new Set<string>();
    const borrowerSet = new Set<string>();
    rows.forEach((r) => {
      creditorSet.add(r.source);
      borrowerSet.add(r.target);
    });
    const creditors = [...creditorSet].sort(
      (a, b) => (totals[b] ?? 0) - (totals[a] ?? 0),
    );
    const borrowers = [...borrowerSet].sort(
      (a, b) => (totals[b] ?? 0) - (totals[a] ?? 0),
    );

    const maxTotal = Math.max(...Object.values(totals));
    const scaleSymbol = (v: number) =>
      v > 0 ? 18 + (Math.sqrt(v) / Math.sqrt(maxTotal)) * 72 : 18;

    // Place creditors (x=200) and borrowers (x=800) in a 1000×1000 canvas
    const CANVAS = 1000;
    const creditorStep = CANVAS / (creditors.length + 1);
    const borrowerStep = CANVAS / (borrowers.length + 1);

    const colorByRegionName = new Map<string, string>();
    let nextColorIndex = 0;

    const getRegionColor = (rawName: string) => {
      const regionName = stripPrefix(rawName);
      const existingColor = colorByRegionName.get(regionName);
      if (existingColor) {
        return existingColor;
      }
      const assignedColor = NODE_COLORS[nextColorIndex % NODE_COLORS.length];
      colorByRegionName.set(regionName, assignedColor);
      nextColorIndex += 1;
      return assignedColor;
    };

    const creditorNodes = creditors.map((name, i) => ({
      id: name,
      name: stripPrefix(name),
      x: 200,
      y: creditorStep * (i + 1),
      symbolSize: scaleSymbol(totals[name] ?? 0),
      itemStyle: { color: getRegionColor(name) },
      label: {
        show: true,
        position: "left" as const,
        fontSize: "0.9rem",
        color: "#374151",
      },
    }));

    const borrowerNodes = borrowers.map((name, i) => ({
      id: name,
      name: stripPrefix(name),
      x: 800,
      y: borrowerStep * (i + 1),
      symbolSize: scaleSymbol(totals[name] ?? 0),
      itemStyle: {
        color: getRegionColor(name),
      },
      label: {
        show: true,
        position: "right" as const,
        fontSize: "0.9rem",
        color: "#374151",
      },
    }));

    const maxFlow = Math.max(
      ...rows.map((r) => r["SUM(TotalCommitmentAmount)"] as number),
    );

    const links = rows.map((row) => {
      const val = row["SUM(TotalCommitmentAmount)"] as number;
      return {
        source: row.source,
        target: row.target,
        value: val,
        lineStyle: {
          width: 0.5 + (val / maxFlow) * 9,
          color: "#94a3b8",
          opacity: 0.45,
          curveness: 0.2,
        },
      };
    });

    return {
      tooltip: {
        trigger: "item",
        formatter: (params: unknown) => {
          const p = params as {
            dataType?: string;
            name?: string;
            data?: {
              id?: string;
              source?: string;
              target?: string;
              value?: number;
            };
          };
          if (p.dataType === "edge" && p.data) {
            const from = stripPrefix(p.data.source ?? "");
            const to = stripPrefix(p.data.target ?? "");
            return `${from} → ${to}<br/><b>${formatBillions(p.data.value ?? 0)}</b>`;
          }
          if (p.dataType === "node" && p.data) {
            const total = totals[p.data.id ?? ""] ?? 0;
            return `<b>${p.name}</b><br/>Total: ${formatBillions(total)}`;
          }
          return "";
        },
      },
      series: [
        {
          type: "graph",
          layout: "none",
          roam: false,
          draggable: false,
          data: [...creditorNodes, ...borrowerNodes],
          links,
          edgeSymbol: ["none", "none"],
          emphasis: {
            focus: "adjacency",
          },
        },
      ],
    } as EChartsOption;
  }, []);

  return (
    <Card sx={{ px: "1rem", py: "1rem" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            mb: 1,
          }}
        >
          <Typography
            sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#111827" }}
          >
            Regional Debt Flow Network
          </Typography>
          <Box
            component="img"
            src="/SDF-Logo.png"
            alt="Sovereign Debt Forum logo"
            sx={{
              width: { xs: 92, sm: 150 },
              height: "auto",
              flexShrink: 0,
              opacity: 0.7,
            }}
          />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ position: "relative", pt: "4rem" }}>
          <Box
            sx={{
              position: "absolute",
              top: 30,
              left: 0,
              right: 0,
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            <Typography
              sx={{
                position: "absolute",
                left: "32%",
                transform: "translateX(-50%)",
                fontSize: "1rem",
                fontWeight: 700,
                letterSpacing: "0.02em",
                color: "#4b5563",
                textTransform: "uppercase",
              }}
            >
              Creditors
            </Typography>
            <Typography
              sx={{
                position: "absolute",
                left: "68%",
                transform: "translateX(-50%)",
                fontSize: "1rem",
                fontWeight: 700,
                letterSpacing: "0.02em",
                color: "#4b5563",
                textTransform: "uppercase",
              }}
            >
              Borrowers
            </Typography>
          </Box>
          <ReactECharts option={option} style={{ height: 500 }} />
        </Box>
        <Typography
          sx={{
            mt: 1,
            fontSize: "0.8rem",
            color: "#111827",
            textAlign: "center",
          }}
        >
          {/* Circle size represents total commitment volume. Line thickness shows
          flow amount. Hover for details. */}
          This data reflects the documents available on our platform, not all
          sovereign debt.
        </Typography>
      </CardContent>
    </Card>
  );
}
